import { useState, useEffect, type FormEvent, type DragEvent } from 'react'
import { useMutation, useClient } from 'urql'
import { gql } from '../gql/gql'
import type { NewItemInput } from '../gql/graphql'
import { uploadFile, type UploadResult } from '../lib/file-upload'

import { Button } from './ui/button'
import { Input } from './ui/input'
import { DateInput } from './ui/date-input'
import { Category, CATEGORY_DISPLAY, CATEGORY_EMOJIS } from '../lib/constants'
import { MapPin, X, UploadCloud, Loader2, Trash2 } from 'lucide-react'
import { cn, todayLocalDateString } from '@/lib/utils'
import { FileUploader } from './file-uploader'
import { useConsent } from '../context/consent-context'

const CHECK_DUPLICATE_QUERY = gql(`
  query CheckDuplicateTransaction($merchant: String!, $purchasedAt: String!, $total: Float!) {
    checkDuplicateTransaction(merchant: $merchant, purchasedAt: $purchasedAt, total: $total) {
      id
    }
  }
`)

const ADD_ITEMS_MUTATION = gql(`
  mutation AddItems($input: [NewItemInput!]!) {
    addItems(input: $input) {
      id
      name
      amount
      purchasedAt
      merchant
      category
      latitude
      longitude
      receiptUrl
      receiptFilename
      receiptContentType
      receiptSize
    }
  }
`)

interface AddExpenseFormProps {
  onSuccess: () => void
  onCancel?: () => void
  currencySymbol?: string
}

interface LineItem {
  name: string
  amount: string
  category: string
}

const emptyItem = (): LineItem => ({ name: '', amount: '', category: 'OTHER' })

export function AddExpenseForm({ onSuccess, onCancel, currencySymbol = '$' }: AddExpenseFormProps) {
  const { aiConsent, grantConsent } = useConsent()
  const [lineItems, setLineItems] = useState<LineItem[]>([emptyItem()])
  const [merchant, setMerchant] = useState('')
  const [purchasedAt, setPurchasedAt] = useState(todayLocalDateString)
  const [link, setLink] = useState('')
  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [receiptTotal, setReceiptTotal] = useState<number | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [showMerchant, setShowMerchant] = useState(false)
  const [showLink, setShowLink] = useState(false)
  const [isLocating, setIsLocating] = useState(false)
  const [error, setError] = useState('')
  const [amountErrors, setAmountErrors] = useState<Record<number, string>>({})
  const [pendingAddInput, setPendingAddInput] = useState<NewItemInput[] | null>(null)
  const [{ fetching }, addItems] = useMutation(ADD_ITEMS_MUTATION)
  const client = useClient()

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { setLatitude(pos.coords.latitude); setLongitude(pos.coords.longitude) },
        () => {}
      )
    }
  }, [])

  const handleFileSelect = async (file: File | null) => {
    setReceiptFile(file)
    setUploadResult(null)
    setIsDraggingOver(false)
    if (!file) {
      setLineItems([emptyItem()])
      setMerchant('')
      setShowMerchant(false)
      setPurchasedAt(todayLocalDateString())
      return
    }

    setIsScanning(true)
    setError('')
    try {
      const result = await uploadFile(file)
      setUploadResult(result)
      if (result.parsed) {
        const p = result.parsed
        if (p.merchant) { setMerchant(p.merchant); setShowMerchant(true) }
        if (p.purchasedAt) {
          const parsed = new Date(p.purchasedAt + 'T12:00:00')
          const year = parsed.getFullYear()
          if (!isNaN(parsed.getTime()) && year >= 2000 && year <= new Date().getFullYear() + 1) {
            setPurchasedAt(p.purchasedAt)
          }
        }
        if (p.total != null) setReceiptTotal(p.total)
        if (p.lineItems?.length) {
          const items = p.lineItems.map(i => ({
            name: i.name,
            amount: i.amount.toFixed(2),
            category: i.category ?? 'OTHER',
          }))
          setLineItems(items)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload receipt')
      setReceiptFile(null)
    } finally {
      setIsScanning(false)
    }
  }

  const onFormDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (aiConsent !== false && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setIsDraggingOver(true) }
  }
  const onFormDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false)
  }
  const onFormDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f && (f.type.startsWith('image/') || f.type === 'application/pdf')) handleFileSelect(f)
  }

  const handleLocation = () => {
    if (!('geolocation' in navigator)) return
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      (p) => { setLatitude(p.coords.latitude); setLongitude(p.coords.longitude); setIsLocating(false) },
      () => setIsLocating(false)
    )
  }

  const updateItem = (index: number, field: keyof LineItem, value: string) => {
    setLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item))
  }

  const toggleItemCategory = (index: number, cat: string) => {
    setLineItems(prev => prev.map((item, i) =>
      i === index ? { ...item, category: item.category === cat ? '' : cat } : item
    ))
  }

  const addItem = () => setLineItems(prev => [...prev, emptyItem()])

  const removeItem = (index: number) => {
    if (lineItems.length === 1) return
    setLineItems(prev => prev.filter((_, i) => i !== index))
  }

  const buildInput = (finalUpload: UploadResult | null) =>
    lineItems.map(item => ({
      name: item.name,
      amount: parseFloat(item.amount),
      purchasedAt,
      merchant: merchant || null,
      link: link || null,
      category: item.category || null,
      latitude,
      longitude,
      receiptUrl: finalUpload?.key ?? null,
      receiptFilename: finalUpload?.filename ?? null,
      receiptContentType: finalUpload?.contentType ?? null,
      receiptSize: finalUpload?.size ?? null,
      receiptTotal: receiptTotal ?? null,
    }))

  const submitInput = async (input: NewItemInput[]) => {
    const result = await addItems({ input })
    if (result.error) { setError(result.error.message); return }
    if (result.data?.addItems) onSuccess()
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    const newAmountErrors: Record<number, string> = {}
    lineItems.forEach((item, index) => {
      const v = parseFloat(item.amount)
      if (isNaN(v) || v === 0) newAmountErrors[index] = 'Enter a valid amount'
      else if (v > 9_999_999 || v < -9_999_999) newAmountErrors[index] = 'Amount is too large'
    })
    setAmountErrors(newAmountErrors)
    if (Object.keys(newAmountErrors).length > 0) return

    let finalUpload = uploadResult
    if (receiptFile && !finalUpload) {
      try {
        finalUpload = await uploadFile(receiptFile)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload receipt')
        return
      }
    }

    const input = buildInput(finalUpload)

    if (merchant) {
      const total = receiptTotal ?? lineItems.reduce((sum, item) => sum + parseFloat(item.amount), 0)
      const { data } = await client.query(CHECK_DUPLICATE_QUERY, { merchant, purchasedAt, total }).toPromise()
      if (data?.checkDuplicateTransaction) {
        setPendingAddInput(input as NewItemInput[])
        return
      }
    }

    await submitInput(input as NewItemInput[])
  }

  return (
    <div
      className="relative"
      onDragOver={onFormDragOver}
      onDragLeave={onFormDragLeave}
      onDrop={onFormDrop}
    >
      {isDraggingOver && !receiptFile && (
        <div className="absolute inset-0 z-10 rounded-lg border-2 border-primary border-dashed bg-primary/5 flex flex-col items-center justify-center gap-2 pointer-events-none">
          <UploadCloud className="h-8 w-8 text-primary" />
          <p className="text-sm font-medium text-primary">Drop to attach receipt</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Receipt + shared fields */}
        {aiConsent === false ? (
          <div className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm font-medium text-foreground">AI receipt scanning is off</p>
            <p className="text-xs text-muted-foreground">Enable AI features to snap a receipt and have your purchases fill in automatically.</p>
            <button type="button" onClick={grantConsent} className="text-xs text-primary underline">Enable AI Features</button>
          </div>
        ) : (
          <FileUploader value={receiptFile} onFileSelect={handleFileSelect} onDrop={() => setIsDraggingOver(false)} isScanning={isScanning} />
        )}

        {isScanning && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Scanning receipt…</span>
          </div>
        )}

        {/* Shared: merchant + date */}
        <div className="flex gap-2">
          {showMerchant ? (
            <Input
              placeholder="Merchant (e.g. Costco)"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              className="flex-1 h-11 text-sm"
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowMerchant(true)}
              className="flex-1 h-11 px-3 text-sm text-left border border-border rounded-md text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
            >
              + Add merchant
            </button>
          )}
          <DateInput value={purchasedAt} onChange={setPurchasedAt} />
        </div>

        {/* Line items */}
        <div className="space-y-3">
          {lineItems.map((item, index) => (
            <div key={index} className="space-y-1.5 border border-border rounded-lg p-3">
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="What was purchased?"
                  value={item.name}
                  onChange={(e) => updateItem(index, 'name', e.target.value)}
                  className="flex-1 h-10 text-sm"
                  required
                />
                <div className="flex flex-col items-end flex-shrink-0">
                  <div className="relative w-28">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm select-none">
                      {currencySymbol}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={item.amount}
                      onChange={(e) => {
                        updateItem(index, 'amount', e.target.value)
                        if (amountErrors[index]) setAmountErrors(prev => { const n = { ...prev }; delete n[index]; return n })
                      }}
                      className={`pl-7 h-10 text-sm ${amountErrors[index] ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                      required
                    />
                  </div>
                  {amountErrors[index] && (
                    <p className="text-xs text-destructive mt-1">{amountErrors[index]}</p>
                  )}
                </div>
                {lineItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0 flex items-center gap-1 text-xs"
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only sm:not-sr-only">Remove</span>
                  </button>
                )}
              </div>

              {/* Per-item category chips */}
              <div className="flex flex-wrap gap-1">
                {Object.values(Category).map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleItemCategory(index, cat)}
                    className={cn(
                      'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors',
                      item.category === cat
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                    )}
                  >
                    <span>{CATEGORY_EMOJIS[cat]}</span>
                    <span>{CATEGORY_DISPLAY[cat]}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addItem}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          + Add item
        </button>

        {/* Optional link */}
        <div className="space-y-2">
          {showLink && (
            <Input
              type="url"
              placeholder="https://..."
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
          )}
          {!showLink && (
            <button type="button" onClick={() => setShowLink(true)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              + Add link
            </button>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-2">
          {latitude !== null && longitude !== null ? (
            <span className="flex items-center gap-1 text-xs bg-muted rounded-full px-2.5 py-1">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span>Location set</span>
              <button type="button" onClick={() => { setLatitude(null); setLongitude(null) }} className="text-muted-foreground hover:text-destructive flex-shrink-0">
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={handleLocation} disabled={isLocating} className="gap-1.5 h-7 text-xs">
              <MapPin className="h-3.5 w-3.5" />
              {isLocating ? 'Locating…' : 'Add location'}
            </Button>
          )}
        </div>

        {pendingAddInput && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 space-y-2">
            <p className="font-medium">This looks similar to an expense you already added.</p>
            <div className="flex gap-2 justify-center">
              <Button
                type="button"
                size="sm"
                onClick={async () => { setPendingAddInput(null); await submitInput(pendingAddInput) }}
              >
                Add anyway
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setPendingAddInput(null); onCancel?.() }}
              >
                Go back
              </Button>
            </div>
          </div>
        )}

        {!pendingAddInput && (
          <Button type="submit" className="w-full" disabled={fetching || isScanning}>
            {fetching
              ? 'Saving…'
              : lineItems.length === 1
                ? 'Add Expense'
                : `Add ${lineItems.length} Items`}
          </Button>
        )}
      </form>
    </div>
  )
}
